import { Controller } from "@hotwired/stimulus"

// reimbursable-list — lista dinâmica de reembolsáveis [nome + valor] ("rachar a
// conta"). Adiciona/remove linhas a partir de um <template>. Os names ficam no
// formato reimbursements[N][person_name|amount_cents]; o índice N não precisa ser
// contíguo (o controller Rails itera sobre os valores, buracos são ignorados).
//
// Primeiro campo repetível/nested-form do projeto — padrão de referência.
// CSP-safe: só data-action, nenhum JS inline. O mask_controller reconecta sozinho
// nas linhas clonadas (Stimulus observa o DOM).
//
// Uso: data-controller="reimbursable-list" com targets rows (container) e template
// (<template> da linha, com __INDEX__ no lugar do índice).
export default class extends Controller {
  static targets = ["rows", "template", "destroyFlag"]
  static values = {
    autofill: { type: Boolean, default: false },
    // Valor (string "60,08") com que a PRIMEIRA linha nasce — o valor inteiro do
    // item na importação (caso comum: a compra toda foi de outra pessoa). Vazio = off.
    firstFill: { type: String, default: "" }
  }

  connect() {
    // Índice inicial parte do nº de linhas já renderizadas (na importação, os
    // reembolsáveis existentes ocupam 0..N-1; novas continuam a partir daí).
    this.index = this.hasRowsTarget ? this.rowsTarget.children.length : 0
    // autofill: começa com 1 linha vazia quando não há nenhuma (forms de compra/avulso).
    if (this.autofillValue && this.hasRowsTarget && this.rowsTarget.children.length === 0) this.addRow()
  }

  addRow() {
    const isFirst = this.hasRowsTarget && this.rowsTarget.children.length === 0
    const html = this.templateTarget.innerHTML.replaceAll("__INDEX__", this._nextIndex())
    this.rowsTarget.insertAdjacentHTML("beforeend", html)
    // Primeira linha da importação: pré-preenche o valor com o total do item para
    // o usuário só escolher a pessoa. Rachar = editar. Dispara input p/ a máscara.
    if (isFirst && this.firstFillValue) {
      const amount = this.rowsTarget.lastElementChild?.querySelector('input[name*="amount_cents"]')
      if (amount && !amount.value) {
        amount.value = this.firstFillValue
        amount.dispatchEvent(new Event("input", { bubbles: true }))
      }
    }
  }

  // Auto-save GOVERNADO pela completude da linha: o change de um campo de reembolsável
  // (nome/valor) NÃO deve acionar o save da linha enquanto a row está incompleta — senão
  // o re-render do servidor descarta a row em edição (reject_if) e ela some. Só deixa o
  // save acontecer quando a row do evento tem pessoa E valor. stopPropagation impede o
  // change cru de subir ao form (statement-line#save escuta por bubbling).
  maybeSave(event) {
    event.stopPropagation()
    const row = event.target.closest("[data-reimbursable-list-row]")
    if (!row) return
    const person = row.querySelector('input[name*="[person_name]"]')?.value?.trim()
    const amount = row.querySelector('input[name*="[amount_cents]"]')?.value?.trim()
    if (!person || !amount) return   // incompleta: mantém em edição, não salva ainda
    // Completa: aciona o auto-save da linha (o statement-line escuta este evento).
    this.element.dispatchEvent(new CustomEvent("reimbursable-list:save", { bubbles: true }))
  }

  // Linha NOVA (sem registro no banco): remove do DOM. Linha EXISTENTE (tem o
  // destroyFlag hidden): marca _destroy=1 e esconde — o nested attributes apaga o
  // registro na próxima gravação. Dispara change para o auto-save (import) captar.
  removeRow(event) {
    const row = event.target.closest("[data-reimbursable-list-row]")
    if (!row) return
    const flag = row.querySelector("[data-reimbursable-list-target='destroyFlag']")
    if (flag) {
      // Registro EXISTENTE: marca _destroy=1, esconde e salva (remoção é mudança real).
      flag.value = "1"
      row.classList.add("hidden")
      this.element.dispatchEvent(new CustomEvent("reimbursable-list:save", { bubbles: true }))
    } else {
      // Linha NOVA nunca salva (só existe no DOM): remover é só tirar do DOM.
      row.remove()
    }
  }

  _nextIndex() {
    // Monotônico crescente; buracos são ok (o Rails ignora índices ausentes).
    this.index += 1
    return this.index
  }
}
